import { HttpClient } from '@angular/common/http';
import {
  Injectable,
  PLATFORM_ID,
  Inject,
  signal,
  WritableSignal,
  Injector,
} from '@angular/core';
import { LocalStorageService } from '@app/core/services/local-storage.service';
import { User } from '@app/pages/shared/interfaces/user.model';
import { environment } from '@environments/environment';
import { isPlatformServer } from '@angular/common';
import { Analytics } from '@app/core/services/analytics/analytics.service';
import { EventType } from '@app/core/enums/meta.enum';

@Injectable({
  providedIn: 'root',
})
export class UserCountryService {
  private readonly baseUrl: string = environment.apiUrl;
  _countryCode: WritableSignal<string> = signal('IN');
  private readonly USER_ITEM = '_user';
  private readonly PAGE = 'Initial';

  constructor(
    private readonly http: HttpClient,
    public localStorageService: LocalStorageService,
    @Inject(PLATFORM_ID) private readonly platformId: object, // Inject platform ID for SSR check,
    private readonly injector: Injector // Keep injector but don't resolve Analytics immediately
  ) {}

  private get analytics(): Analytics {
    return this.injector.get(Analytics); // Inject Analytics only when needed
  }

  _getUser(): User | null {
    const user = this.localStorageService.get(this.USER_ITEM);
    return user ? (JSON.parse(user) as User) : null;
  }

  setCountryCode(value: string) {
    if (value) {
      this._countryCode.set(value.toUpperCase());
    }
  }

  initialCountryCodeSetupByIp(): Promise<void> {
    const user = this._getUser();
    return new Promise((resolve, reject) => {
      if (user?.countryCode) {
        this.setCountryCode(user.countryCode);
        resolve();
      } else if (isPlatformServer(this.platformId)) {
        console.warn('IP-based country lookup skipped in SSR environment');
        resolve();
      } else {
        this.http.get('https://api.country.is').subscribe({
          next: (response: any) => {
            this.analytics.customAnalytics('Get Country By IP Success', {
              countryCode: response.country,
              page: this.PAGE,
              eventType: EventType.api,
            });
            this.setCountryCode(response.country);
            resolve();
          },
          error: (error) => {
            console.error('Failed to get country from api.country.is:', error);
            this.analytics.failureEvent('Get Country By IP Failure', {
              error: 'api.country.is API Error',
              page: this.PAGE,
              reason:
                error?.error?.message ??
                error?.message ??
                'Failed to get country from api.country.is',
            });
            this.getCountryCodeFromBackend().then(resolve).catch(reject);
          },
        });
      }
    });
  }

  private getCountryCodeFromBackend(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.http.get('https://api.ipify.org?format=json').subscribe({
        next: (ipResponse: any) => {
          this.analytics.customAnalytics('Get IP Address Success', {
            page: this.PAGE,
            eventType: EventType.api,
          });
          this.http
            .get<{ data: { countryCode: string } }>(
              `${this.baseUrl}/user/ipDetails/${ipResponse.ip}`
            )
            .subscribe({
              next: (res) => {
                if (res?.data?.countryCode) {
                  this.setCountryCode(res.data.countryCode);
                  this.analytics.customAnalytics('Get Country Success', {
                    countryCode: res.data.countryCode,
                    page: this.PAGE,
                    eventType: EventType.api,
                  });
                }
                resolve();
              },
              error: (error) => {
                console.error(
                  'Failed to get country code from backend:',
                  error
                );
                this.analytics.failureEvent('Get Country Failure', {
                  error: '/user/ipDetails/ API Error',
                  page: this.PAGE,
                  reason:
                    error.error?.message ??
                    'Failed to get country code from backend api.',
                });
                reject(new Error(error));
              },
            });
        },
        error: (error) => {
          this.analytics.failureEvent('Get IP Address Failure', {
            error: 'api.ipify.org API Error',
            page: this.PAGE,
            reason:
              error.error?.message ??
              'Failed to get IP address from api.ipify.org',
          });
          console.error('Failed to get IP address from api.ipify.org:', error);
          reject(new Error(error));
        },
      });
    });
  }
}
